// Rechner: Möbelgriffe bohren (PRD §7.6). Interaktive Drag-Vorschau, Griff rastet mittig ein.
import React, { useEffect, useMemo, useState } from "react";
import { calcGrip, fmtCm } from "@/lib/calc";
import { track, trackSettled } from "@/lib/track";
import EditorShell from "./EditorShell";
import { startDrag, round1, clamp, fmt, niceStep } from "@/components/preview/useDragSvg";
import { C, Svg, handle, cross, pill, hDim, vDim } from "@/components/preview/canvasPrimitives";

const DEFAULTS = { doorWidth: "45", doorHeight: "16", spacing: "12", orient: "h", centerX: "22.5", centerY: "8" };

const W = 880;
const H = 470;

export default function GripCalculator() {
  const [v, setV] = useState(DEFAULTS);
  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const setNow = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const setMany = (obj) => setV((s) => ({ ...s, ...obj }));
  const num = (k) => {
    const x = parseFloat(String(v[k]).replace(",", "."));
    return isNaN(x) ? NaN : x;
  };

  const r = useMemo(() => calcGrip(v), [v]);

  useEffect(() => {
    if (!r.ok) return;
    trackSettled("calculator_used", { calculator: "grip", mode: r.orient === "v" ? "vertical" : "horizontal" });
    trackSettled("result_shown", { calculator: "grip", verdict: (r.warnings && r.warnings.length) ? "warn" : "ok" });
  }, [r]);

  const canvas = renderCanvas(v, r, num, setNow, setMany);
  const copyText = r.ok ? buildCopyText(r) : "";

  const fields = [
    { id: "doorWidth", label: "Frontbreite", value: v.doorWidth, step: "0.5", onChange: set("doorWidth") },
    { id: "doorHeight", label: "Fronthöhe", value: v.doorHeight, step: "0.5", onChange: set("doorHeight") },
    { id: "spacing", label: "Griff-Lochabstand", value: v.spacing, step: "0.5", onChange: set("spacing"), hint: "Bohrabstand des Griffs, oft auf dem Griff angegeben." },
  ];

  return (
    <EditorShell
      testId="calc-grip"
      canvas={canvas}
      canvasReady={r.ok}
      emptyHint="Gib Frontmaße und Lochabstand ein, um die Vorschau zu sehen."
      toolbarHint="Ziehe den Griff über die Front (rastet mittig ein), den Lochabstand und die Frontmaße."
      orient={{ value: v.orient, onH: () => setNow("orient", "h"), onV: () => setNow("orient", "v") }}
      onCenter={() => setMany({ centerX: String(round1((num("doorWidth") || 0) / 2)), centerY: String(round1((num("doorHeight") || 0) / 2)) })}
      onReset={() => setV(DEFAULTS)}
      fields={fields}
      warnings={r.ok ? r.warnings : []}
      hasResult={r.ok}
      results={r.ok ? buildResults(r) : []}
      steps={r.ok ? buildSteps(r) : []}
      copyText={copyText}
    />
  );
}

function renderCanvas(v, r, num, setNow, setMany) {
  const els = [];
  if (!r.ok) {
    els.push(
      <text key="x" x={W / 2} y={H / 2} fill={C.dim} fontSize={16} textAnchor="middle" fontFamily="Inter,sans-serif">
        Frontmaße eingeben, um die Vorschau zu sehen.
      </text>
    );
    return (
      <Svg vbW={W} vbH={H} ariaLabel="Vorschau: Möbelgriffe bohren">
        {els}
      </Svg>
    );
  }
  const w = r.doorWidth;
  const hh = r.doorHeight;
  const scale = Math.min((W - 320) / w, (H - 170) / hh);
  const fw = w * scale;
  const fh = hh * scale;
  const cx0 = W / 2;
  const cy0 = H / 2 + 4;
  const fL = cx0 - fw / 2;
  const fT = cy0 - fh / 2;
  const mX = (cm) => fL + cm * scale;
  const mY = (cm) => fT + cm * scale;
  const xToCmX = (px) => (px - fL) / scale;
  const yToCmY = (px) => (px - fT) / scale;

  els.push(<rect key="front" x={fL} y={fT} width={fw} height={fh} rx={6} fill={C.object} stroke={C.objectStroke} strokeWidth={2} />);
  els.push(<rect key="panel" x={fL + 9} y={fT + 9} width={Math.max(0, fw - 18)} height={Math.max(0, fh - 18)} rx={4} fill="none" stroke="hsl(188 72% 28% / 0.25)" strokeWidth={1.2} style={{ pointerEvents: "none" }} />);

  let p1, p2;
  if (r.orient === "h") {
    p1 = { x: mX(r.hole1), y: mY(r.centerY) };
    p2 = { x: mX(r.hole2), y: mY(r.centerY) };
  } else {
    p1 = { x: mX(r.centerX), y: mY(r.hole1) };
    p2 = { x: mX(r.centerX), y: mY(r.hole2) };
  }

  if (r.orient === "h") {
    const by = p1.y - 10;
    els.push(<rect key="lg1" x={p1.x - 2.5} y={by} width={5} height={10} fill="hsl(188 72% 28% / 0.5)" style={{ pointerEvents: "none" }} />);
    els.push(<rect key="lg2" x={p2.x - 2.5} y={by} width={5} height={10} fill="hsl(188 72% 28% / 0.5)" style={{ pointerEvents: "none" }} />);
    els.push(<rect key="bar" x={p1.x - 9} y={by - 7} width={p2.x - p1.x + 18} height={8} rx={4} fill="hsl(188 72% 28% / 0.35)" stroke={C.objectStroke} strokeWidth={1.5} style={{ pointerEvents: "none" }} />);
  } else {
    const bx2 = p1.x - 10;
    els.push(<rect key="lg1" x={bx2} y={p1.y - 2.5} width={10} height={5} fill="hsl(188 72% 28% / 0.5)" style={{ pointerEvents: "none" }} />);
    els.push(<rect key="lg2" x={bx2} y={p2.y - 2.5} width={10} height={5} fill="hsl(188 72% 28% / 0.5)" style={{ pointerEvents: "none" }} />);
    els.push(<rect key="bar" x={bx2 - 7} y={p1.y - 9} width={8} height={p2.y - p1.y + 18} rx={4} fill="hsl(188 72% 28% / 0.35)" stroke={C.objectStroke} strokeWidth={1.5} style={{ pointerEvents: "none" }} />);
  }

  const rb = fT + fh;
  const stepW = niceStep(w);
  const stepH = niceStep(hh);
  for (let cmx = 0; cmx <= w + 0.01; cmx += stepW) {
    const X = mX(cmx);
    els.push(<line key={"gbt" + cmx} x1={X} y1={rb} x2={X} y2={rb + 6} stroke={C.wallStroke} strokeWidth={1} />);
    els.push(
      <text key={"gbl" + cmx} x={X} y={rb + 20} fill={C.dim} fontSize={11} textAnchor="middle" fontFamily="Inter,sans-serif" style={{ fontVariantNumeric: "tabular-nums" }}>
        {fmt(cmx)}
      </text>
    );
  }
  for (let cmy = 0; cmy <= hh + 0.01; cmy += stepH) {
    const Y = mY(cmy);
    els.push(<line key={"glt" + cmy} x1={fL - 6} y1={Y} x2={fL} y2={Y} stroke={C.wallStroke} strokeWidth={1} />);
    els.push(
      <text key={"gll" + cmy} x={fL - 9} y={Y + 4} fill={C.dim} fontSize={11} textAnchor="end" fontFamily="Inter,sans-serif" style={{ fontVariantNumeric: "tabular-nums" }}>
        {fmt(cmy)}
      </text>
    );
  }

  if (r.orient === "h") els.push(hDim("sp", p1.x, p2.x, fT - 22, fmt(r.spacing) + " cm", C.primary, W));
  else els.push(vDim("sp", p1.y, p2.y, fL - 32, fmt(r.spacing) + " cm", C.primary, W));

  els.push(cross("hc0", p1.x, p1.y, null));
  els.push(
    cross("hc1", p2.x, p2.y, (ev) =>
      startDrag(ev, W, H, (cur) => {
        const sp = r.orient === "h" ? 2 * Math.abs(xToCmX(cur.x) - r.centerX) : 2 * Math.abs(yToCmY(cur.y) - r.centerY);
        setNow("spacing", String(round1(clamp(sp, 1, r.orient === "h" ? w : hh))));
      })
    )
  );

  const barC = r.orient === "h" ? { x: (p1.x + p2.x) / 2, y: p1.y - 10 } : { x: p1.x - 10, y: (p1.y + p2.y) / 2 };
  els.push(
    handle("mv", barC.x, barC.y, "move", (ev) =>
      startDrag(ev, W, H, (cur) => {
        let ncx = clamp(xToCmX(cur.x), 0, w);
        let ncy = clamp(yToCmY(cur.y), 0, hh);
        if (Math.abs(ncx - w / 2) < Math.max(0.8, w * 0.04)) ncx = w / 2;
        if (Math.abs(ncy - hh / 2) < Math.max(0.8, hh * 0.05)) ncy = hh / 2;
        setMany({ centerX: String(round1(ncx)), centerY: String(round1(ncy)) });
      }), "h")
  );
  els.push(
    handle("hw", fL + fw, cy0, "ew-resize", (ev) =>
      startDrag(ev, W, H, (cur) => setNow("doorWidth", String(round1(clamp(xToCmX(cur.x), 5, 200))))), "v")
  );
  els.push(
    handle("hh", cx0, fT + fh, "ns-resize", (ev) =>
      startDrag(ev, W, H, (cur) => setNow("doorHeight", String(round1(clamp(yToCmY(cur.y), 3, 200))))), "h")
  );

  return (
    <Svg vbW={W} vbH={H} ariaLabel="Vorschau: Möbelgriffe bohren">
      {els}
    </Svg>
  );
}

function buildResults(r) {
  if (r.orient === "h") {
    return [
      { label: "Bohrloch 1 (von links)", value: fmtCm(r.hole1) },
      { label: "Bohrloch 2 (von links)", value: fmtCm(r.hole2) },
      { label: "Lochabstand", value: fmtCm(r.spacing) },
      { label: "Höhe von der Oberkante", value: fmtCm(r.axisFromTop) },
      { label: "Abstand zur linken Kante", value: fmtCm(r.edgeStart) },
      { label: "Abstand zur rechten Kante", value: fmtCm(r.edgeEnd) },
    ];
  }
  return [
    { label: "Bohrloch 1 (von oben)", value: fmtCm(r.hole1) },
    { label: "Bohrloch 2 (von oben)", value: fmtCm(r.hole2) },
    { label: "Lochabstand", value: fmtCm(r.spacing) },
    { label: "Abstand von der Seitenkante", value: fmtCm(r.axisFromSide) },
    { label: "Abstand zur Oberkante", value: fmtCm(r.edgeStart) },
    { label: "Abstand zur Unterkante", value: fmtCm(r.edgeEnd) },
  ];
}

function buildSteps(r) {
  if (r.orient === "h") {
    return [
      `Markiere Bohrloch 1 bei ${fmtCm(r.hole1)} und Bohrloch 2 bei ${fmtCm(r.hole2)} von der linken Kante.`,
      `Beide auf ${fmtCm(r.axisFromTop)} Höhe von der Oberkante und mit der Wasserwaage waagerecht ausrichten.`,
      `Lochabstand ${fmtCm(r.spacing)} nachmessen, vorbohren, dann durchbohren.`,
    ];
  }
  return [
    `Markiere Bohrloch 1 bei ${fmtCm(r.hole1)} und Bohrloch 2 bei ${fmtCm(r.hole2)} von der Oberkante.`,
    `Beide auf ${fmtCm(r.axisFromSide)} Abstand von der Seitenkante und mit der Wasserwaage senkrecht ausrichten.`,
    `Lochabstand ${fmtCm(r.spacing)} nachmessen, vorbohren, dann durchbohren.`,
  ];
}

function buildCopyText(r) {
  if (r.orient === "h") {
    return [
      "Möbelgriff (horizontal)",
      `Bohrloch 1 (von links): ${fmtCm(r.hole1)}`,
      `Bohrloch 2 (von links): ${fmtCm(r.hole2)}`,
      `Lochabstand: ${fmtCm(r.spacing)}`,
      `Höhe von Oberkante: ${fmtCm(r.axisFromTop)}`,
    ].join("\n");
  }
  return [
    "Möbelgriff (vertikal)",
    `Bohrloch 1 (von oben): ${fmtCm(r.hole1)}`,
    `Bohrloch 2 (von oben): ${fmtCm(r.hole2)}`,
    `Lochabstand: ${fmtCm(r.spacing)}`,
    `Abstand von Seitenkante: ${fmtCm(r.axisFromSide)}`,
  ].join("\n");
}
